import { ChangeEvent, FC, useEffect, useState } from 'react'
import styles from './EditPlatformCardShortInformation.module.css'
import * as yup from 'yup'
import CustomInput from '../UI/CustomInput/CustomInput'
import { Formik, FormikValues } from 'formik'
import TitleBlockForEditPages from '../TitleBlockForEditPages/TitleBlockForEditPages'
import { EstablishmentsType, PlaceType } from '../../redux/actions/types'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { changePlaceMainInformation, getCurrentPlaces } from '../../redux/actions/placesAction'
import { Suggestion } from '../../pages/RegistrationPage/RegistrationPage'
import { getDaDataCity } from '../../redux/actions/daDataAction'
import { clearDadata } from '../../redux/reducers/daData/daDataSlice'
import {
  changeEstablishmentMainInformation,
  getCurrentEstablishment,
} from '../../redux/actions/establishmentsAction'
import CustomTextArea from '../UI/CustomTextArea/CustomTextArea'
import BlockSwiper from '../BlockSwiper/BlockSwiper'
import { useMediaQuery } from 'react-responsive'
import { useToast } from '../../context/ToastContext'

export type EditPlatformCardShortInformationPropsType = {
  platformData: PlaceType | EstablishmentsType
  forEstablishments?: boolean
  onChangeSwipeCheckboxHandler?: (checked: boolean) => void
}

const EditPlatformCardShortInformation: FC<EditPlatformCardShortInformationPropsType> = ({
  platformData,
  forEstablishments,
  onChangeSwipeCheckboxHandler,
}) => {
  const [editMode, setEditMode] = useState(false)

  const [city, setCity] = useState<string>('')
  const [citySuggestions, setCitySuggestions] = useState<Suggestion[]>([])
  const [cityError, setCityError] = useState('')
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const { token } = useAppSelector((state) => state.auth)
  const { daDataCity } = useAppSelector((state) => state.daData)
  const { showSuccess, showError } = useToast()
  const dispatch = useAppDispatch()

  const validationSchema = yup.object().shape({
    city: yup.string().required('Поле не может быть'),
    capacity: yup
      .number()
      .typeError('Введите количество в числовом значении')
      .integer('Вместительность должна быть целым числом')
      .positive('Вместительность должна быть положительной')
      .required('Поле не может быть пустым')
      .test('is-not-zero', 'Вместительность не должна быть равна нулю', (value) => value !== 0),
    platformName: yup.string().required('Поле не может быть'),

  })

  const handleCityInputChange = async (value: string) => {
    setCity(value)

    if (value.trim().length >= 1) {
      await dispatch(getDaDataCity({ query: city }))
    } else {
      setCitySuggestions([])
    }
  }

  const handleSelectSuggestion = (selectedCity: string) => {
    setCity(selectedCity)
    setCitySuggestions([])
  }

  const handleSubmitHandler = async (values: FormikValues) => {
    const resultAction = await dispatch(
      changePlaceMainInformation({
        token,
        placeId: String(platformData.id),
        address: values.address,
        name: values.platformName,
        city: city,
        size: values.capacity,
        establishment: values.establishmentName,
        description: values.description,
      }),
    )

    if (changePlaceMainInformation.rejected.match(resultAction)) {
      showError('Произошла ошибка при изменении данных')
    } else {
      showSuccess('Данные успешно изменены')
      setEditMode(false)
      await dispatch(getCurrentPlaces({ placeId: String(platformData.id), token }))
      await dispatch(clearDadata())
    }
  }

  const handleSubmitHandlerEstablishment = async (values: FormikValues) => {
    console.log
    const resultAction = await dispatch(
      changeEstablishmentMainInformation({
        token,
        establishmentId: String(platformData.id),
        address: values.address,
        name: values.platformName,
        city: city,
        size: values.capacity,
        description: values.description,
      }),
    )
    if (changeEstablishmentMainInformation.rejected.match(resultAction)) {
      showError('Произошла ошибка при изменении данных')
    } else {
      showSuccess('Данные успешно изменены')
      setEditMode(false)
      await dispatch(getCurrentEstablishment({ establishmentId: String(platformData.id), token }))
      await dispatch(clearDadata())
    }
  }

  useEffect(() => {
    setCitySuggestions(daDataCity)
  }, [daDataCity])

  useEffect(() => {
    if (platformData.city) {
      setCity(platformData.city)
    }
  }, [platformData])



  return (
    <div className={styles.editSHortInformation}>
      <Formik
        key={platformData.id}
        initialValues={{
          city: platformData.city ? platformData.city : '',
          address: platformData.address ? platformData.address : '',
          platformName: platformData.name ? platformData.name : '',
          companyName: platformData.company.name ? platformData.company.name : '',
          capacity: platformData.size ? String(platformData.size) : '',
          establishment: platformData.establishment ? platformData?.establishment : '',
          description: platformData.description,
        }}
        validateOnBlur
        onSubmit={(values) =>
          forEstablishments ? handleSubmitHandlerEstablishment(values) : handleSubmitHandler(values)
        }
        validationSchema={validationSchema}
      >
        {(props) => {
          const { values, touched, errors, handleChange, handleBlur } = props

          return (
            <>
              <TitleBlockForEditPages
                title={'Основная информация'}
                disabled={false}
                handleSubmit={() =>
                  forEstablishments
                    ? handleSubmitHandlerEstablishment(values)
                    : handleSubmitHandler(values)
                }
              />
              <div className={styles.formContainer}>
                <div className={styles.inputBlock}>
                  <h4 className={styles.subtitle}>{`Название ${
                    forEstablishments ? 'площадок' : 'залы'
                  }`}</h4>
                  <CustomInput
                    type='text'
                    placeholder=''
                    name='platformName'
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.platformName}
                    className={
                      errors.platformName && touched.platformName
                        ? `${styles.inputError} ${styles.input}`
                        : styles.input
                    }
                  />
                </div>
                <div className={styles.inputBlock}>
                  <h4 className={styles.subtitle}>Город</h4>
                  <div className={styles.rightBlock}>
                    <CustomInput
                      type='text'
                      placeholder=''
                      name='city'
                      required
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleCityInputChange(e.target.value)
                      }
                      value={city}
                      className={cityError ? `${styles.inputError} ${styles.input}` : styles.input}
                    />
                    <ul className={styles.suggestionsDropdown}>
                      {citySuggestions.map((suggestion: Suggestion, index: number) => (
                        <li
                          key={index}
                          onClick={() => handleSelectSuggestion(suggestion.data.city)}
                        >
                          {suggestion.data.city}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={styles.inputBlock}>
                  <h4 className={styles.subtitle}>Адрес</h4>
                  <CustomInput
                    type='text'
                    placeholder=''
                    name='address'
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                    className={
                      errors.address && touched.address
                        ? `${styles.inputError} ${styles.input}`
                        : styles.input
                    }
                  />
                </div>
                <div className={styles.inputBlock}>
                  <h4 className={styles.subtitle}>Компания</h4>
                  <div className={styles.companyBlock}>{values.companyName}</div>
                </div>
                {values.establishment && (
                  <div className={styles.inputBlock}>
                    <h4 className={styles.subtitle}>Площадка</h4>
                    <div className={styles.companyBlock}>
                      {(values.establishment as EstablishmentsType).name}
                    </div>
                  </div>
                )}
                {!forEstablishments && (
                  <div className={styles.inputBlock}>
                    <h4 className={styles.subtitle}>Вместительность</h4>
                    <CustomInput
                      type='text'
                      placeholder=''
                      name='capacity'
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.capacity}
                      className={
                        errors.capacity && touched.capacity
                          ? `${styles.inputError} ${styles.input}`
                          : styles.input
                      }
                    />
                  </div>
                )}

                <div style={{ alignItems: 'flex-start' }} className={styles.inputBlock}>
                  <h4 className={styles.subtitle}>Описание</h4>
                  <CustomTextArea
                    withoutFile
                    placeholder='Описание'
                    name='description'
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                  />
                </div>

                {onChangeSwipeCheckboxHandler && 'isBlocked' in platformData && (
                  <div
                    style={{
                      alignItems: 'flex-start',
                      flexDirection: 'row',
                      justifyContent: isMobile ? 'space-between' : 'flex-start',
                    }}
                    className={styles.inputBlock}
                  >
                    <h4 className={styles.subtitle}>Заблокировать</h4>
                    <BlockSwiper
                      isBlocked={platformData.isBlocked}
                      onChangeHandler={onChangeSwipeCheckboxHandler}
                    />{' '}
                  </div>
                )}
              </div>
            </>
          )
        }}
      </Formik>
    </div>
  )
}

export default EditPlatformCardShortInformation
